import { Component, Input, forwardRef, inject, signal, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { FileInputComponent } from './file-upload.component';
import { DigitalSignatureComponent, DigitalSignatureData } from './digital-signature/digital-signature.component';
import { UtilService } from '@services/util.service';
import { LoginService, UserDetails } from '@services/login.service';
import { RelationshipType, Consent, ConsentType, ConsentCategory } from '@models/consent.model';
import { MeterDetails } from '@models/meter.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-add-hh-consent',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    FileInputComponent,
    DigitalSignatureComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddHhConsentComponent),
      multi: true
    }
  ],
  template: `
    <div class="documentation-section">
      <!-- Proof of Ownership section for landlords and company representatives -->
      @if (relationshipType === 'landlord') {
        <div class="proof-ownership-section">
          <h5>Proof of Ownership</h5>
          <p class="audit-notice">
            Please note that we conduct random audits to verify ownership and may require you to provide us with a copy of your Title register, which you can download from the Land Registry.
          </p>
        </div>
      }
      
      <div class="loa-section">
        <h5>Consent Document</h5>
        <p>
          Please choose one of the following options to provide your consent to access half-hourly data:
        </p>
        
        <div class="consent-options">
          <!-- Radio button selection -->
          <mat-radio-group class="consent-radio-group" [(ngModel)]="selectedConsentMethod" (change)="onConsentMethodChange()">
            <mat-radio-button value="digital" class="consent-radio-option">
              <span class="radio-label">
                Digital Signature 
              </span>
            </mat-radio-button>
            <mat-radio-button value="upload" class="consent-radio-option">
              <span class="radio-label">Manual signature</span>
            </mat-radio-button>
          </mat-radio-group>

          <!-- Digital signature option -->
          @if (selectedConsentMethod === 'digital') {
            <div class="consent-option">
              <app-digital-signature
                [isVerified]="isDigitalSignatureVerified()"
                [signatureData]="getDigitalSignatureData()"
                (consentReceived)="onDigitalSignatureConsentReceived($event)"
                (loadingStateChange)="onDigitalSignatureLoadingChange($event)"
                (error)="onDigitalSignatureError($event)">
              </app-digital-signature>
            </div>
          }

          <!-- Upload LoA option -->
          @if (selectedConsentMethod === 'upload') {
            <div class="consent-option">
              <h6>Manual signature</h6>
              <p>Download the provided template, sign it, and upload the completed document.</p>
              @if(relationshipType === 'occupier') {
                <p>
                  <a target="_blank" download="LoA_Perse_template.docx" 
                    href="https://s3.eu-west-1.amazonaws.com/assets.perse.io/files/pdf/LoA_Consent_Wallet_June2025_Consumer.docx">
                    Download Letter of Authority Template (.docx)
                  </a>
                </p>
                <p>
                  <a target="_blank" download="LoA_Perse_template.pdf" 
                    href="https://s3.eu-west-1.amazonaws.com/assets.perse.io/files/pdf/LoA_Consent_Wallet_June2025_Consumer.pdf">
                    Download Letter of Authority Template (.pdf)
                  </a>
                </p>
              } @else {
                <p>
                  <a target="_blank" download="LoA_Perse_template.docx" 
                    href="https://s3.eu-west-1.amazonaws.com/assets.perse.io/files/pdf/LoA_Consent_Wallet_June2025_Owner.docx">
                    Download Letter of Authority Template (.docx)
                  </a>
                </p>
                <p>
                  <a target="_blank" download="LoA_Perse_template.pdf" 
                    href="https://s3.eu-west-1.amazonaws.com/assets.perse.io/files/pdf/LoA_Consent_Wallet_June2025_Owner.pdf">
                    Download Letter of Authority Template (.pdf)
                  </a>
                </p>
              }
           
              <div class="consent-input-container">
                <app-file-input 
                  accept=".pdf"
                  [formControl]="fileUploadControl"
                  #fileUpload>
                </app-file-input>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .documentation-section {
      margin: 1rem 0;
    }
    
    .proof-ownership-section {
      margin-bottom: 1.5rem;
    }
    
    .audit-notice {
      font-style: italic;
      color: #666;
    }
    
    .loa-section h5 {
      margin-bottom: 0.5rem;
    }
    
    .consent-options {
      margin-top: 1rem;
    }
    
    .consent-radio-group {
      // margin-bottom: 1.5rem;
      display: flex;
      // flex-direction: column;
      // gap: 0.75rem;
    }
    
    .consent-radio-option {
      margin-bottom: 0.5rem;
    }
    
    .radio-label {
      font-weight: 500;
      font-size: 1rem;
    }
    
    .recommended-badge {
      color: #4caf50;
      font-weight: 600;
      font-size: 0.875rem;
    }
    
    .consent-option {
      margin-top: 1rem;
      padding: 1rem;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      background-color: #fafafa;
    }
    
    .consent-option h6 {
      margin-bottom: 0.5rem;
      font-weight: 600;
    }
    
    .consent-option p {
      margin-bottom: 0.5rem;
    }
    
    .consent-option a {
      color: #1976d2;
      text-decoration: none;
    }
    
    .consent-option a:hover {
      text-decoration: underline;
    }
    
    .consent-input-container {
      margin-top: 1rem;
    }
  `]
})
export class AddHhConsentComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() relationshipType: RelationshipType = RelationshipType.OCCUPIER;
  @Input() selectedMeters: MeterDetails[] = [];
  
  @ViewChild('fileUpload') fileUploadComponent!: FileInputComponent;

  private utilService = inject(UtilService);
  private loginService = inject(LoginService);
  
  // Internal value - now standardized to always be a Consent object
  private _value: Consent | null = null;
  
  // Control value accessor methods - standardized to work with Consent type
  private _onChange = (value: Consent | null) => {};
  private _onTouched = () => {};
  
  // Loading states
  requestingDigitalSignature = signal(false);
  
  // Consent method selection (digital signature is default/recommended)
  selectedConsentMethod: 'digital' | 'upload' = 'digital';
  
  // FormControl for file upload
  fileUploadControl = new FormControl<File | null>(null);
  private fileUploadSubscription?: Subscription;
  
  // Get logged in user details
  get loggedInUser(): UserDetails | null {
    return this.loginService.loginUser;
  }

  ngAfterViewInit(): void {
    // Set up file upload subscription using valueChanges
    this.setupFileUploadSubscription();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.fileUploadSubscription?.unsubscribe();
  }

  writeValue(value: Consent | null): void {
    this._value = value;
    
    // If it's a LoA file consent, switch to upload mode and set the file
    if (value?.consentType === ConsentType.LOA_UPLOAD && value.consentData instanceof File) {
      this.selectedConsentMethod = 'upload';
      this.fileUploadControl.setValue(value.consentData);
    } else if (value?.consentType === ConsentType.DIGITAL_SIGNATURE && typeof value.consentData === 'string') {
      // Digital signature consent - ensure we're in digital mode
      this.selectedConsentMethod = 'digital';
      this.fileUploadControl.setValue(null);
    } else {
      // Clear file upload for any other case
      this.fileUploadControl.setValue(null);
    }
  }

  registerOnChange(fn: (value: Consent | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.fileUploadControl.disable();
    } else {
      this.fileUploadControl.enable();
    }
  }

  // Helper method to check if digital signature is verified
  isDigitalSignatureVerified(): boolean {
    // For digital signature, consent type will be DIGITAL_SIGNATURE with string consentData
    return !!(this._value && 
             this._value.consentType === ConsentType.DIGITAL_SIGNATURE);
  }

  // Helper methods to create standardized Consent objects
  // This ensures the form control always returns a value of Consent type
  private createLoaFileConsent(file: File): Consent {
    return {
      consentCategory: ConsentCategory.LOA,
      consentType: ConsentType.LOA_UPLOAD,
      consentData: file,
      scope: 'HH'
    };
  }

  private createDigitalSignatureConsent(consentId: string): Consent {
    return {
      consentCategory: ConsentCategory.LOA,
      consentType: ConsentType.DIGITAL_SIGNATURE,
      consentData: consentId,
      scope: 'HH'
    };
  }

  // Get digital signature data for the component
  getDigitalSignatureData(): DigitalSignatureData {
    return {
      selectedMeters: this.selectedMeters,
      userDetails: this.loggedInUser,
      relationshipType: this.relationshipType
    };
  }

  // Handle consent method change
  onConsentMethodChange(): void {
    // Clear any existing consent value when switching methods
    this._value = null;
    this._onChange(null);
    this._onTouched();
    
    // Clear file upload when switching away from upload method
    if (this.selectedConsentMethod !== 'upload') {
      this.fileUploadControl.setValue(null);
    }
  }

  private setupFileUploadSubscription(): void {
    // Clean up existing subscription
    this.fileUploadSubscription?.unsubscribe();
    
    // Subscribe to file upload control value changes
    this.fileUploadSubscription = this.fileUploadControl.valueChanges.subscribe((file: File | null) => {
      if (this.selectedConsentMethod === 'upload') {
        if (file) {
          const consent = this.createLoaFileConsent(file);
          this._value = consent;
          this._onChange(consent);
          this._onTouched();
        } else {
          this._value = null;
          this._onChange(null);
        }
      }
    });
  }

  // Handle digital signature consent received
  onDigitalSignatureConsentReceived(consentId: string): void {
    console.log('Digital signature consent received:', consentId);
    const consent = this.createDigitalSignatureConsent(consentId);
    this._value = consent;
    this._onChange(consent);
    this._onTouched();
  }

  // Handle digital signature loading state change
  onDigitalSignatureLoadingChange(isLoading: boolean): void {
    this.requestingDigitalSignature.set(isLoading);
  }

  // Handle digital signature errors
  onDigitalSignatureError(errorMessage: string): void {
    this.utilService.openSnackBar(errorMessage, 'Close', 'error');
  }
}
